import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Col, Empty, Input, Radio, Row, Space, Spin, Tag, Typography, message } from "antd";
import { CheckCircleOutlined, CrownOutlined, ShopOutlined } from "@ant-design/icons";
import { getMembershipProducts, getMyUpgradeRequest, submitUpgradeRequest } from "../services/api";

const { Title, Text, Paragraph } = Typography;
const roleNames = { landlord: "房东", agent: "中介" };
const statusNames = { pending: "待管理员审核", approved: "审核已通过", rejected: "审核未通过" };
const statusColors = { pending: "gold", approved: "green", rejected: "red" };

const MembershipScreen = ({ user }) => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [products, setProducts] = useState([]);
    const [latest, setLatest] = useState(null);
    const [targetRole, setTargetRole] = useState("landlord");
    const [selectedId, setSelectedId] = useState(null);
    const [note, setNote] = useState("");

    const loadData = async () => {
        try {
            setLoading(true);
            const [productData, requestData] = await Promise.all([getMembershipProducts(), getMyUpgradeRequest()]);
            setProducts(Array.isArray(productData) ? productData : []);
            setLatest(requestData?.request || null);
        } catch (error) {
            message.error(error?.error || "会员信息加载失败");
        } finally { setLoading(false); }
    };

    useEffect(() => { loadData(); }, []);
    const visibleProducts = useMemo(() => products.filter(item => item.target_role === targetRole), [products, targetRole]);
    const hasPending = latest?.status === "pending";

    const submit = async () => {
        if (!selectedId) return message.warning("请先选择一个会员方案");
        try {
            setSubmitting(true);
            await submitUpgradeRequest({ target_role: targetRole, product_id: selectedId, note });
            message.success("申请已提交，请等待管理员审核");
            await loadData();
        } catch (error) { message.error(error?.error || "提交失败"); }
        finally { setSubmitting(false); }
    };

    if (user?.role !== "user") return <Alert showIcon type="info" message="当前账号已不是普通用户" description="房东、中介和管理员账号无需再次申请升级。" />;

    return (
        <div className="membership-page">
            <div className="membership-heading">
                <Title level={2}><CrownOutlined /> 升级账号</Title>
                <Text type="secondary">选择会员方案，提交后由平台管理员审核</Text>
            </div>
            {latest && <Alert className="membership-request-alert" showIcon type={latest.status === "approved" ? "success" : latest.status === "rejected" ? "error" : "warning"} message={<span>最近申请：升级为{roleNames[latest.target_role]} <Tag color={statusColors[latest.status]}>{statusNames[latest.status]}</Tag></span>} description={latest.admin_note ? `管理员说明：${latest.admin_note}` : hasPending ? "管理员处理前无法重复提交。" : "您可以根据需要重新选择方案提交。"} />}
            <Card className="membership-role-card" title="第一步：选择升级身份">
                <Radio.Group value={targetRole} buttonStyle="solid" onChange={event => { setTargetRole(event.target.value); setSelectedId(null); }} disabled={hasPending}>
                    <Radio.Button value="landlord">升级为房东</Radio.Button>
                    <Radio.Button value="agent">升级为中介</Radio.Button>
                </Radio.Group>
            </Card>
            <Card className="membership-products-card" title={<span><ShopOutlined /> 第二步：选择会员方案</span>}>
                <Spin spinning={loading}>
                    {visibleProducts.length ? <Row gutter={[16, 16]}>{visibleProducts.map(product => (
                        <Col xs={24} md={12} xl={8} key={product.id}>
                            <Card hoverable className={`membership-product ${selectedId === product.id ? "selected" : ""}`} onClick={() => !hasPending && setSelectedId(product.id)}>
                                <div className="membership-product-top"><Tag color={product.target_role === "landlord" ? "blue" : "purple"}>{roleNames[product.target_role]}会员</Tag>{selectedId === product.id && <CheckCircleOutlined />}</div>
                                <Title level={3}>{product.name}</Title>
                                <div className="membership-price"><span>¥</span>{Number(product.price || 0).toFixed(2)}</div>
                                <Text type="secondary">{product.duration_days > 0 ? `有效期 ${product.duration_days} 天` : "长期有效"}</Text>
                                <Paragraph>{product.description || "以平台发布的会员权益为准"}</Paragraph>
                                <Space direction="vertical" size={5}>{(product.features || []).map((feature, index) => <Text key={index}><CheckCircleOutlined style={{ color: "#52c41a" }} /> {feature}</Text>)}</Space>
                            </Card>
                        </Col>
                    ))}</Row> : !loading && <Empty description="管理员暂未上架该身份的会员方案" />}
                </Spin>
            </Card>
            <Card className="membership-submit-card" title="第三步：提交审核">
                <Input.TextArea value={note} onChange={event => setNote(event.target.value)} maxLength={500} showCount rows={3} disabled={hasPending} placeholder="可填写申请说明，例如您的出租或从业情况（选填）" />
                <Button type="primary" size="large" icon={<CrownOutlined />} loading={submitting} disabled={hasPending || !selectedId} onClick={submit}>{hasPending ? "申请审核中" : "提交升级申请"}</Button>
            </Card>
        </div>
    );
};

export default MembershipScreen;
